import { useLazyQuery } from '@apollo/client';
import { Empty, Select, Spin } from 'antd';
import { debounce, uniqBy } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LIMIT, SORT } from '../../common/constants';
import { MY_LIBRARY_STAFF } from '../../libraryModules/libraryStaff/graphql/Queries';
import SelectComponent from '../SelectComponent';
import { formatStaffName } from './constants';
import { LIBRARY_STAFF_USERS_ADMIN } from './graphql/Queries';

const { Option } = Select;

const LOADING_OPTION_KEY = '__loading__';

const LibraryStaffSelect = ({
  role,
  libraryId,
  isLibrary = false,
  currentUser,
  loading: isSaving = false,
  onChange,
  onDropdownVisibleChange,
  ...rest
}) => {
  const [adminStaff, setAdminStaff] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const hasFetched = useRef(false);
  const skipRef = useRef(0);
  const searchRef = useRef('');
  const requestIdRef = useRef(0);

  const [fetchAdminStaff] = useLazyQuery(LIBRARY_STAFF_USERS_ADMIN, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [
    fetchLibraryStaff,
    { data: libraryData, loading: isLibraryLoading, error: libraryError },
  ] = useLazyQuery(MY_LIBRARY_STAFF, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const loadAdminStaff = useCallback(
    async (skip = 0, search = '') => {
      const requestId = requestIdRef.current;
      try {
        const result = await fetchAdminStaff({
          variables: {
            filter: {
              skip,
              limit: LIMIT,
              search,
              roles: [role],
              staffLibraryIds: [libraryId],
              isActive: true,
              isArchived: false,
            },
            sort: { field: 'createdAt', order: SORT?.DESC },
          },
        });
        if (requestId !== requestIdRef.current) return;

        const rows = result?.data?.users?.data || [];
        const total = result?.data?.users?.count || 0;
        setAdminStaff((prev) =>
          skip === 0 ? rows : uniqBy([...prev, ...rows], 'id'),
        );
        setHasMore(skip + rows?.length < total);
      } catch (error) {
        if (requestId === requestIdRef.current) setHasMore(false);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsInitialLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [fetchAdminStaff, role, libraryId],
  );

  const startAdminSearch = (search = '') => {
    requestIdRef.current += 1;
    skipRef.current = 0;
    searchRef.current = search;
    setAdminStaff([]);
    setHasMore(true);
    setIsLoadingMore(false);
    setIsInitialLoading(true);
    loadAdminStaff(0, search);
  };

  const canFetch = Boolean(role && (isLibrary || libraryId));

  const handleDropdownVisibleChange = (visible) => {
    if (visible && !hasFetched.current && canFetch) {
      hasFetched.current = true;
      if (isLibrary) {
        fetchLibraryStaff();
      } else {
        startAdminSearch('');
      }
    }
    onDropdownVisibleChange?.(visible);
  };

  const handleAdminSearch = useCallback(
    debounce((search) => {
      if (!canFetch) return;
      hasFetched.current = true;
      startAdminSearch(search?.trim());
    }, 500),
    [loadAdminStaff, canFetch],
  );

  useEffect(() => () => handleAdminSearch?.cancel?.(), [handleAdminSearch]);

  const handlePopupScroll = (event) => {
    const { target } = event;
    if (
      !isInitialLoading &&
      !isLoadingMore &&
      hasMore &&
      target?.scrollTop + target?.offsetHeight >= target?.scrollHeight - 20
    ) {
      const nextSkip = skipRef.current + LIMIT;
      skipRef.current = nextSkip;
      setIsLoadingMore(true);
      loadAdminStaff(nextSkip, searchRef.current);
    }
  };

  const handleChange = (value, option) => {
    setSelectedUser(option?.user || null);
    onChange?.(value, option);
  };

  let showListLoader = false;
  if (canFetch) {
    showListLoader = isLibrary
      ? isLibraryLoading || !(libraryData || libraryError)
      : isInitialLoading;
  }

  const staffList = useMemo(() => {
    const users = isLibrary
      ? (libraryData?.library?.staff || [])
          ?.filter((staff) => staff?.role === role && staff?.user?.isActive)
          ?.map((staff) => staff?.user)
      : adminStaff;

    return uniqBy(
      [currentUser, selectedUser, ...users]?.filter((user) => user?.id),
      'id',
    );
  }, [isLibrary, libraryData, adminStaff, role, currentUser, selectedUser]);

  return (
    <SelectComponent
      showSearch
      allowClear={false}
      loading={isInitialLoading || isLibraryLoading || isSaving}
      optionFilterProp="children"
      filterOption={isLibrary}
      notFoundContent={<Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />}
      dropdownRender={(menu) =>
        showListLoader ? (
          <div className="d-flex justify-center p-16">
            <Spin size="small" />
          </div>
        ) : (
          menu
        )
      }
      onSearch={isLibrary ? undefined : handleAdminSearch}
      onPopupScroll={isLibrary ? undefined : handlePopupScroll}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      onChange={handleChange}
      {...rest}
    >
      {staffList?.map((user) => (
        <Option key={user?.id} value={user?.id} user={user}>
          {formatStaffName(user)}
        </Option>
      ))}
      {isLoadingMore && (
        <Option key={LOADING_OPTION_KEY} value={LOADING_OPTION_KEY} disabled>
          <div className="select-load-more">
            <Spin size="small" />
          </div>
        </Option>
      )}
    </SelectComponent>
  );
};

export default LibraryStaffSelect;
